import { HttpClient } from '@angular/common/http';
import { AfterViewChecked, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ConfirmService } from 'src/app/services/confirm.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';

export interface DialogFotoPerfilData {
  idUsuario?: number;
  abrirSeletorAoIniciar?: boolean;
  arquivoInicial?: File;
}

@Component({
  selector: 'app-dialog-foto-perfil',
  templateUrl: './dialog-foto-perfil.component.html',
  styleUrls: ['./dialog-foto-perfil.component.css']
})
export class DialogFotoPerfilComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('fileInput') public fileInput!: ElementRef<HTMLInputElement>;
  public imagemAtual?: SafeUrl;
  public imagemOriginal?: HTMLImageElement;
  public preview?: string;
  public carregando = true;
  public salvando = false;
  public zoom = 1;
  public posicaoX = 50;
  public posicaoY = 50;
  private subscription?: Subscription;
  private imagemAtualObjectUrl?: string;
  private arraste?: { x: number; y: number; posicaoX: number; posicaoY: number; };
  private seletorAberto = false;

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogFotoPerfilData | null,
    private httpClient: HttpClient,
    private dialogRef: MatDialogRef<DialogFotoPerfilComponent>,
    private snackBarService: SnackBarService,
    private confirmService: ConfirmService,
    private sanitizer: DomSanitizer
  ) { }

  public ngOnInit(): void {
    this.carregarImagemAtual();
    if (this.data?.arquivoInicial) this.carregarArquivo(this.data.arquivoInicial);
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (this.imagemAtualObjectUrl) URL.revokeObjectURL(this.imagemAtualObjectUrl);
    if (this.preview?.startsWith('blob:')) URL.revokeObjectURL(this.preview);
  }

  public ngAfterViewChecked(): void {
    if (this.data?.abrirSeletorAoIniciar && !this.carregando && !this.seletorAberto) {
      this.seletorAberto = true;
      this.fileInput.nativeElement.click();
    }
  }

  public selecionarArquivo(event: Event): void {
    const arquivo = (event.target as HTMLInputElement).files?.[0];
    if (arquivo) this.carregarArquivo(arquivo);
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    const arquivo = event.dataTransfer?.files?.[0];
    if (arquivo) this.carregarArquivo(arquivo);
  }

  public larguraPreview(): number {
    return this.proporcaoLargura() * this.zoom * 100;
  }

  public alturaPreview(): number {
    return this.proporcaoAltura() * this.zoom * 100;
  }

  public deslocamentoPreviewX(): number {
    return -(this.larguraPreview() - 100) * this.posicaoX / 100;
  }

  public deslocamentoPreviewY(): number {
    return -(this.alturaPreview() - 100) * this.posicaoY / 100;
  }

  public iniciarArraste(event: PointerEvent): void {
    if (!this.imagemOriginal) return;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.arraste = { x: event.clientX, y: event.clientY, posicaoX: this.posicaoX, posicaoY: this.posicaoY };
  }

  public arrastar(event: PointerEvent): void {
    if (!this.arraste || !this.imagemOriginal) return;
    const area = event.currentTarget as HTMLElement;
    const ladoExibido = area.getBoundingClientRect().width;
    const ladoRecorte = Math.min(this.imagemOriginal.naturalWidth, this.imagemOriginal.naturalHeight) / this.zoom;
    const limiteX = this.imagemOriginal.naturalWidth - ladoRecorte;
    const limiteY = this.imagemOriginal.naturalHeight - ladoRecorte;

    this.posicaoX = limiteX > 0
      ? this.limitar(this.arraste.posicaoX - ((event.clientX - this.arraste.x) / ladoExibido) * (ladoRecorte / limiteX) * 100, 0, 100)
      : 50;
    this.posicaoY = limiteY > 0
      ? this.limitar(this.arraste.posicaoY - ((event.clientY - this.arraste.y) / ladoExibido) * (ladoRecorte / limiteY) * 100, 0, 100)
      : 50;
  }

  public encerrarArraste(): void {
    this.arraste = undefined;
  }

  public salvar(): void {
    if (!this.imagemOriginal || this.salvando) return;
    this.salvando = true;
    this.gerarArquivoOtimizado().then(arquivo => {
      const dados = new FormData();
      dados.append('imagem', arquivo);
      this.subscription?.unsubscribe();
      this.subscription = this.httpClient.post(this.urlFotoPerfil(), dados).subscribe({
        next: () => {
          this.salvando = false;
          this.snackBarService.exibirSucesso('Foto de perfil atualizada com sucesso!');
          this.dialogRef.close(true);
        },
        error: erro => {
          this.salvando = false;
          if (erro?.status === 413) {
            this.snackBarService.exibirErro('A foto selecionada excede o tamanho permitido.');
            return;
          }
          this.snackBarService.exibirErro(erro?.error ?? 'Não foi possível salvar a foto de perfil.');
        }
      });
    }).catch(() => {
      this.salvando = false;
      this.snackBarService.exibirErro('Não foi possível processar esta imagem.');
    });
  }

  public remover(): void {
    this.confirmService.confirm('Deseja remover a foto de perfil?', 'Alerta', 'Remover').subscribe(confirmado => {
      if (!confirmado) return;
      this.salvando = true;
      this.subscription?.unsubscribe();
      this.subscription = this.httpClient.delete(this.urlFotoPerfil()).subscribe({
        next: () => {
          this.salvando = false;
          this.snackBarService.exibirSucesso('Foto de perfil removida.');
          this.dialogRef.close(true);
        },
        error: () => {
          this.salvando = false;
          this.snackBarService.exibirErro('Não foi possível remover a foto de perfil.');
        }
      });
    });
  }

  public cancelar(): void {
    if (!this.salvando) this.dialogRef.close();
  }

  private carregarImagemAtual(): void {
    this.subscription = this.httpClient.get(this.urlFotoPerfil(), { responseType: 'blob' }).subscribe({
      next: blob => {
        this.imagemAtualObjectUrl = URL.createObjectURL(blob);
        this.imagemAtual = this.sanitizer.bypassSecurityTrustUrl(this.imagemAtualObjectUrl);
        this.carregando = false;
      },
      error: () => this.carregando = false
    });
  }

  private carregarArquivo(arquivo: File): void {
    if (!arquivo.type.startsWith('image/')) {
      this.snackBarService.exibirErro('Selecione um arquivo de imagem.');
      return;
    }
    const leitor = new FileReader();
    leitor.onload = () => {
      const imagem = new Image();
      imagem.onload = () => {
        this.imagemOriginal = imagem;
        this.zoom = 1;
        this.posicaoX = 50;
        this.posicaoY = 50;
        this.preview = leitor.result as string;
      };
      imagem.onerror = () => this.snackBarService.exibirErro('Não foi possível abrir esta imagem.');
      imagem.src = leitor.result as string;
    };
    leitor.readAsDataURL(arquivo);
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  private criarCanvas(tamanho = 1024): HTMLCanvasElement {
    const imagem = this.imagemOriginal!;
    const ladoOriginal = Math.min(imagem.naturalWidth, imagem.naturalHeight);
    const ladoRecorte = ladoOriginal / this.zoom;
    const origemX = (imagem.naturalWidth - ladoRecorte) * (this.posicaoX / 100);
    const origemY = (imagem.naturalHeight - ladoRecorte) * (this.posicaoY / 100);
    const canvas = document.createElement('canvas');
    canvas.width = tamanho;
    canvas.height = tamanho;
    canvas.getContext('2d')!.drawImage(imagem, origemX, origemY, ladoRecorte, ladoRecorte, 0, 0, tamanho, tamanho);
    return canvas;
  }

  private async gerarArquivoOtimizado(): Promise<File> {
    let tamanho = 1024;
    let qualidade = .92;
    let blob: Blob | null = null;
    do {
      const canvas = this.criarCanvas(tamanho);
      blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/webp', qualidade));
      if (blob && blob.size <= 10 * 1024 * 1024) break;
      if (qualidade > .5) qualidade -= .1;
      else tamanho = Math.floor(tamanho * .75);
    } while (tamanho >= 256);

    if (!blob || blob.size > 10 * 1024 * 1024) throw new Error('imagem muito grande');
    return new File([blob], 'foto-perfil.webp', { type: 'image/webp' });
  }

  private urlFotoPerfil(): string {
    return this.data?.idUsuario ? `/api/Usuarios/${this.data.idUsuario}/FotoPerfil` : '/api/Usuarios/MinhaFotoPerfil';
  }

  private limitar(valor: number, minimo: number, maximo: number): number {
    return Math.min(maximo, Math.max(minimo, valor));
  }

  private proporcaoLargura(): number {
    if (!this.imagemOriginal) return 1;
    return this.imagemOriginal.naturalWidth / Math.min(this.imagemOriginal.naturalWidth, this.imagemOriginal.naturalHeight);
  }

  private proporcaoAltura(): number {
    if (!this.imagemOriginal) return 1;
    return this.imagemOriginal.naturalHeight / Math.min(this.imagemOriginal.naturalWidth, this.imagemOriginal.naturalHeight);
  }
}
